import { useEffect, useId, useRef, useState } from "react";
import { ledgerFetch } from "@/lib/ledgerDemoSource";

// Hover / focus shows HOW a calculated figure was reached (lib/ledgerCalc.js builds the text from the page's own rows).
// `compute(data)` -> {lines, differs} | null. With `load` (async -> data) the rows are fetched once on first show and
// again only when `cacheKey` changes (an edit). The tooltip is aria-describedby on the focusable wrapper; long lists scroll inside.
export default function CalcHover({ compute, load = null, cacheKey = 0, children, align = "right", className = "" }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ key: null, data: undefined, error: false });
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const need = load && state.key !== cacheKey;
  const show = () => {
    setOpen(true);
    if (!need) return;
    const key = cacheKey;
    setState({ key, data: undefined, error: false });
    load().then(
      (data) => { if (alive.current) setState({ key, data, error: false }); },
      () => { if (alive.current) setState({ key, data: undefined, error: true }); },
    );
  };

  let body;
  if (load && state.error) body = { lines: ["Could not load the rows for this figure."] };
  else if (load && state.data === undefined) body = { lines: ["Loading…"] };
  else body = compute(load ? state.data : undefined);

  return (
    <span
      className={`relative inline-block ${className}`}
      tabIndex={0}
      aria-describedby={open && body ? id : undefined}
      data-testid="calc-hover"
      onMouseEnter={show}
      onMouseLeave={() => setOpen(false)}
      onFocus={show}
      onBlur={() => setOpen(false)}
      onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}
    >
      {children}
      {open && body && (
        <span
          id={id}
          role="tooltip"
          data-testid="calc-tooltip"
          onClick={(e) => e.stopPropagation()}
          className={`absolute z-50 top-full mt-1 ${align === "right" ? "right-0" : "left-0"} w-80 max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white p-2 text-left text-[11px] font-normal normal-case tracking-normal leading-snug text-gray-700 shadow-lg whitespace-normal`}
        >
          {body.lines.map((l, i) => (
            <span key={i} className={`block ${l.startsWith("differs") ? "font-semibold text-red-700" : ""}`}>{l}</span>
          ))}
        </span>
      )}
    </span>
  );
}

// Timeline points of one leaf (optionally one position) or of several leaves pooled; labels name the source when pooled.
export async function fetchPoints(sources) {
  const token = localStorage.getItem("token");
  const parts = await Promise.all(sources.map(async ({ leafId, fundName, investorName, positionKey = null, label = "" }) => {
    const qs = new URLSearchParams({ fund_name: fundName || "", investor_name: investorName || "", leaf: leafId });
    if (positionKey != null) qs.set("position_key", positionKey);
    const r = await ledgerFetch(`/api/ledger/timeline?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const j = await r.json();
    return (j.points || []).map((p) => {
      const pid = p.position_id || "";
      return { ...p, pos: `${leafId}|${pid}`, label: [label, pid].filter(Boolean).join(" / ") };
    });
  }));
  return parts.flat();
}
